/** @format */

import { RootState } from "../store/store";
import { formatCurrency } from "../../utility/currencyUtils";

// ==================== Fee Revenue Selectors ====================

export const selectFeeRevenue = (state: RootState) =>
	state.adminRevenue.feeRevenue;

export const selectFeeRevenueSummary = (state: RootState) =>
	state.adminRevenue.feeRevenue?.summary;

export const selectLateFeeRecords = (state: RootState) =>
	state.adminRevenue.feeRevenue?.lateFeeRecords;

export const selectPayoutFeeRecords = (state: RootState) =>
	state.adminRevenue.feeRevenue?.payoutFeeRecords;

export const selectFeeRevenueByMonth = (state: RootState) =>
	state.adminRevenue.feeRevenue?.byMonth;

export const selectFeeRevenueByCircle = (state: RootState) =>
	state.adminRevenue.feeRevenue?.byCircle;

export const selectFeeRevenueByMember = (state: RootState) =>
	state.adminRevenue.feeRevenue?.byMember;

export const selectFeeRevenueFilter = (state: RootState) =>
	state.adminRevenue.feeRevenue?.filter;

export const selectIsLoadingFeeRevenue = (state: RootState) =>
	state.adminRevenue.isLoadingFeeRevenue;

export const selectFeeRevenueError = (state: RootState) =>
	state.adminRevenue.feeRevenueError;

// ==================== Stripe Balance Selectors ====================

export const selectStripeBalance = (state: RootState) =>
	state.adminRevenue.stripeBalance;

export const selectStripeBalanceAvailable = (state: RootState) =>
	state.adminRevenue.stripeBalance?.available;

export const selectStripeBalancePending = (state: RootState) =>
	state.adminRevenue.stripeBalance?.pending;

export const selectStripeBalanceTotal = (state: RootState) =>
	state.adminRevenue.stripeBalance?.total;

export const selectIsLoadingStripeBalance = (state: RootState) =>
	state.adminRevenue.isLoadingStripeBalance;

export const selectStripeBalanceError = (state: RootState) =>
	state.adminRevenue.stripeBalanceError;

// ==================== Stripe Activity Selectors ====================

export const selectStripeActivity = (state: RootState) =>
	state.adminRevenue.stripeActivity;

export const selectStripeCharges = (state: RootState) =>
	state.adminRevenue.stripeActivity?.charges;

export const selectStripeTransfers = (state: RootState) =>
	state.adminRevenue.stripeActivity?.transfers;

export const selectStripeNetRetained = (state: RootState) =>
	state.adminRevenue.stripeActivity?.netRetained;

export const selectIsLoadingStripeActivity = (state: RootState) =>
	state.adminRevenue.isLoadingStripeActivity;

export const selectStripeActivityError = (state: RootState) =>
	state.adminRevenue.stripeActivityError;

// ==================== General Selectors ====================

export const selectIsLoadingRevenue = (state: RootState) =>
	state.adminRevenue.isLoading;

export const selectRevenueError = (state: RootState) =>
	state.adminRevenue.error;

export const selectRevenueLastRefreshedAt = (state: RootState) =>
	state.adminRevenue.lastRefreshedAt;

// ==================== Computed Selectors ====================

/**
 * Get total platform revenue (late fees + payout fees) in dollars
 */
export const selectTotalPlatformRevenue = (state: RootState) =>
	state.adminRevenue.feeRevenue?.summary?.grandTotal?.dollars ?? formatCurrency(0);

/**
 * Get total late fees collected in dollars
 */
export const selectTotalLateFees = (state: RootState) =>
	state.adminRevenue.feeRevenue?.summary?.lateFees?.totalDollars ?? formatCurrency(0);

/**
 * Get total payout fees collected in dollars
 */
export const selectTotalPayoutFees = (state: RootState) =>
	state.adminRevenue.feeRevenue?.summary?.payoutFees?.totalDollars ?? formatCurrency(0);

/**
 * Get count of late fee transactions
 */
export const selectLateFeeCount = (state: RootState) =>
	state.adminRevenue.feeRevenue?.summary?.lateFees?.count ?? 0;

/**
 * Get count of payout fee transactions
 */
export const selectPayoutFeeCount = (state: RootState) =>
	state.adminRevenue.feeRevenue?.summary?.payoutFees?.count ?? 0;

/**
 * Check if any revenue data is loading
 */
export const selectIsAnyRevenueLoading = (state: RootState) =>
	state.adminRevenue.isLoadingFeeRevenue ||
	state.adminRevenue.isLoadingStripeBalance ||
	state.adminRevenue.isLoadingStripeActivity;

/**
 * Check if any revenue errors exist
 */
export const selectHasRevenueErrors = (state: RootState) =>
	!!state.adminRevenue.feeRevenueError ||
	!!state.adminRevenue.stripeBalanceError ||
	!!state.adminRevenue.stripeActivityError;
